import type { DocsContent } from "./types";

const jp = {
  locale: "jp",
  shell: {
    navigationLabel: "ドキュメントナビゲーション",
    browse: "ガイドを見る",
    title: "Comfort ガイド",
    description: "原則からファウンデーションまで順に確認できます。",
    readDesign: "DESIGN.md を読む",
    paginationLabel: "前後のドキュメント",
    previous: "前へ",
    next: "次へ",
    reviewed: "最終確認 · 2026年8月",
    outlineLabel: "ページ内目次",
    outlineTitle: "このページの内容",
    overviewGroup: "はじめに",
    sections: {
      principles: "原則",
      foundations: "ファウンデーション",
    },
  },
  search: {
    ariaLabel: "一覧を検索",
    label: "名前から探す",
    placeholder: "名前や用途を入力してください",
    reset: "検索をリセット",
    result: "件",
    results: "件",
  },
  principles: {
    eyebrow: "デザイン原則",
    title: "心地よいプロダクトをつくる4つの基準",
    description:
      "Comfortが目指すのは、柔らかな見た目だけではありません。次の操作がわかり、結果を予測でき、慣れた使い方のまま機能を広げられる体験です。",
    outlineModel: "全体像",
    outlineReview: "公開前の確認",
    intent: "なぜ必要か",
    practice: "どう使うか",
    modelLabel: "4つのComfort原則がつながる流れ",
    modelCenter: "明確さが生む信頼",
    reviewBadge: "公開前チェック",
    reviewTitle: "新しいパターンを公開する前に確認しましょう",
    reviewItems: [
      "色だけに頼らず、文言・配置・状態から次の操作がわかります。",
      "似たコンポーネントと同じ動きをし、semantic tokenを使っています。",
      "操作した場所の近くにフィードバックが表示され、次の選択肢がわかります。",
      "覚えた使い方を変えずに、高度な機能を段階的に表示できます。",
    ],
    items: [
      {
        id: "natural",
        number: "01",
        title: "自然に",
        comfortTitle: "説明がなくても次の操作が見える",
        summary:
          "すでに馴染みのある言葉と動きを使い、考える負担と操作の手間を減らします。",
        practice:
          "わかりやすい言葉を使い、位置関係を保ち、主な操作を視線が集まる場所に置きます。",
        question: "説明を読まなくても次に何をするかわかりますか？",
      },
      {
        id: "certain",
        number: "02",
        title: "予測できる",
        comfortTitle: "同じ状況には同じ方法で応える",
        summary:
          "繰り返されるルールと必要な選択肢に絞ることで、迷わず操作できます。",
        practice:
          "semantic tokenを使い、似たコンポーネントには同じ状態とフィードバックを用意します。",
        question: "操作する前に結果を予測できますか？",
      },
      {
        id: "meaningful",
        number: "03",
        title: "目的に沿う",
        comfortTitle: "大切な作業に視線を集める",
        summary:
          "見た目と操作は、目的を明確にし、進捗を示し、困ったときの戻り方を伝えるために使います。",
        practice:
          "中心となる作業を目立たせ、操作の近くにフィードバックを返し、判断を邪魔する装飾を減らします。",
        question: "作業の完了や現在の状態の理解に役立っていますか？",
      },
      {
        id: "growing",
        number: "04",
        title: "ともに育つ",
        comfortTitle: "最初は簡単に、慣れたらより深く",
        summary:
          "初めてでも役立ち、使うほど機能を見つけられ、驚かせる変更なしに広げられる設計にします。",
        practice:
          "高度な機能は段階的に見せ、覚えた使い方を保ち、フィードバックを次の改善に生かします。",
        question: "機能が増えても、覚えた使い方をそのまま使えますか？",
      },
    ],
  },
  foundations: {
    eyebrow: "ファウンデーション",
    title: "画面をまたいで使う基準をまとめました",
    description:
      "色、文字、余白、モーションなど、繰り返し使う基準です。まず役割を決め、必要な分だけ組み合わせます。",
    outlineCatalog: "ファウンデーションを探す",
    outlineDirectory: "すべての一覧",
    outlineLayers: "適用の流れ",
    featuredNote: "例と使い方",
    emptyTitle: "該当するファウンデーションがありません",
    emptyDescription: "短い名前や広い用途で検索してください。",
    directoryEyebrow: "15のファウンデーション",
    directoryTitle: "すべての一覧",
    layersBadge: "適用の流れ",
    layersTitle: "意図から実際の画面へ",
    layersDescription: "前の段階で決めた基準が、次の選択を簡単にします。",
    layers: [
      ["01", "原則", "なぜこの動きにするのかを決めます"],
      ["02", "Semantic token", "テーマが変わっても保つ役割を決めます"],
      ["03", "コンポーネント", "役割を再利用できるUIにします"],
    ],
  },
  foundationDetail: {
    eyebrow: "ファウンデーション",
    overview: "プレビュー",
    intent: "用途",
    guidelines: "使い方",
    accessibility: "アクセシビリティ",
    reference: "参考値",
    intentEyebrow: "この基準が必要な理由",
    intentTitle: "何を揃えるための基準か確認しましょう",
    guidanceBadge: "基本ガイド",
    guidanceTitle: "プロダクトで確認するポイント",
    accessibilityEyebrow: "誰でも使えるように",
    accessibilityTitle: "アクセシビリティの確認",
    referenceTitle: "参考値",
    referenceDescription:
      "役割名は保ち、値はテーマ、画面幅、言語、操作状態に合わせて調整できます。",
    role: "役割",
    use: "用途",
    value: "参考値",
    sourceTitle: "詳しくは DESIGN.md へ",
    sourceDescription:
      "実際に使う値と確認項目はDESIGN.mdで管理します。このページでは見た目と動きを確認できます。",
    overviewDescription: "ファウンデーション一覧",
  },
  notFound: {
    title: "ページが見つかりません",
    description:
      "ホームに戻り、原則またはファウンデーションから探してください。",
    action: "Comfortホームへ",
  },
  foundationCopy: {
    "design-token": {
      description:
        "テーマや製品が変わっても決定を持ち運べるセマンティックな語彙です。",
      intent:
        "見た目より先に目的を定義し、意味を変えずにコンポーネントが適応できるようにします。",
      rules: [
        "意図が伝わる役割名を付けます。",
        "エイリアスは一つのソースを通して接続します。",
        "ライトとダークの役割を並行に保ちます。",
      ],
      uses: ["アクションとフォーカス", "ページの地", "コンテンツの面"],
      accessibility: [
        "トークンが変わってもコントラストを保ちます。",
        "状態の役割を色だけに任せません。",
      ],
    },
    color: {
      description:
        "落ち着いたニュートラルが静かな構造を作り、一つの青がインタラクションを示します。",
      intent: "色を装飾にせず、階層・アクション・状態を明確にします。",
      rules: [
        "青はインタラクションだけに使います。",
        "セマンティックな状態色は変えません。",
        "両方のテーマで確認します。",
      ],
      uses: ["ライトのアクション", "ダークのアクション", "本文コンテンツ"],
      accessibility: [
        "本文テキストは4.5対1のコントラストを満たします。",
        "テキスト以外のコントロールは3対1を満たします。",
      ],
    },
    typography: {
      description:
        "製品コンテンツ、補助テキスト、コードのための簡潔なGeistスケールです。",
      intent: "面を足す前に、サイズ・太さ・行長・間隔で階層を伝えます。",
      rules: [
        "文らしい表記を使います。",
        "見出しはバランスよく、本文は自然に折り返します。",
        "モノスペースは機能的なデータに限定します。",
      ],
      uses: ["ヒーローの一文", "製品コピー", "メタデータ"],
      accessibility: [
        "200%拡大でもテキストが流し直されます。",
        "CJKでは意味のまとまりを保ちます。",
      ],
    },
    iconography: {
      description:
        "一貫した幾何学アイコンが、ラベルの代わりにならずに走査を助けます。",
      intent:
        "一つの視覚文法を保ち、馴染みのないアイコンではテキストに意味を運ばせます。",
      rules: [
        "線の太さを一定に保ちます。",
        "光学的な整列を使います。",
        "ありきたりな比喩は避けます。",
      ],
      uses: ["インラインの補助", "標準コントロール", "イラスト用途"],
      accessibility: [
        "装飾アイコンは支援技術から隠します。",
        "アイコンだけのコントロールにはアクセシブルな名前を付けます。",
      ],
    },
    elevation: {
      description:
        "面のコントラストと控えめな影が、実際の重なり関係を説明します。",
      intent: "すべての領域を浮かせずに、包含と重なりを示します。",
      rules: [
        "影より先にトーンを使います。",
        "影を重なり順に合わせます。",
        "光源の方向は一つに保ちます。",
      ],
      uses: [
        "ページコンテンツ",
        "カードとバー",
        "メニューとポップオーバー",
        "ダイアログ",
      ],
      accessibility: [
        "選択状態をエレベーションだけに任せません。",
        "ハイコントラストでも境界が保たれます。",
      ],
    },
    gradient: {
      description:
        "制御されたランプは、データ、マスク、定められたヒーローテキストにだけ使います。",
      intent: "連続性そのものが情報を伝えるときだけ連続した色を使います。",
      rules: [
        "インターフェイスの背景はフラットに保ちます。",
        "データランプにはラベルを付けます。",
        "装飾的なAIグラデーションは避けます。",
      ],
      uses: ["量の順序", "端の連続", "見出しの強調"],
      accessibility: [
        "ランプの両端がコントラスト基準を満たします。",
        "データには色以外の手がかりも用意します。",
      ],
    },
    "inclusive-design": {
      description:
        "セマンティクス、コントラスト、フォーカス、寛容な操作がすべてのコンポーネントを形づくります。",
      intent:
        "知覚、入力、拡大、モーション設定が違っても主要なタスクを操作できるようにします。",
      rules: [
        "ネイティブのセマンティクスを優先します。",
        "フォーカスを常に見えるように保ちます。",
        "失敗のそばに回復手段を設計します。",
      ],
      uses: ["タッチターゲット", "見えるフォーカスリング", "最小幅"],
      accessibility: [
        "WCAG 2.2 AAが基準線です。",
        "支援技術でも同じタスク順序をたどれます。",
      ],
    },
    "international-design": {
      description:
        "翻訳、書字方向、ロケール固有の形式が変わってもレイアウトとコンテンツが保たれます。",
      intent:
        "言語の伸長と方向の切り替えを、例外ではなく通常の製品状態として扱います。",
      rules: [
        "文言が30%伸びる余裕を確保します。",
        "論理プロパティを使います。",
        "日付と数値はロケールに合わせて表記します。",
      ],
      uses: ["文言の余白", "レイアウトの論理", "形式の表記"],
      accessibility: [
        "言語と方向の属性を正しく指定します。",
        "翻訳されたラベルが欠けません。",
      ],
    },
    layout: {
      description:
        "レスポンシブなコンテナと安定した読み順が、タスクの優先度を守ります。",
      intent:
        "意味を変えたり主要なアクションを予測不能に動かしたりせずに、密度とカラムを調整します。",
      rules: [
        "ソース順から始めます。",
        "多軸レイアウトにはグリッドを使います。",
        "読みやすい行長を超えません。",
      ],
      uses: ["コンパクトなページ", "読みの行長", "最大シェル"],
      accessibility: [
        "320pxの幅でもページがあふれません。",
        "拡大してもタスクの順序が保たれます。",
      ],
    },
    motion: {
      description: "原因、方向、連続性が状態の変化を理解しやすくします。",
      intent:
        "入力を遅らせず、装飾的な移動も足さずに、何が変わったかを説明します。",
      rules: [
        "100ms以内にフィードバックを始めます。",
        "transformとopacityだけをアニメーションします。",
        "モーション軽減でも同じ情報を提供します。",
      ],
      uses: ["押下フィードバック", "状態変化", "パネル移動"],
      accessibility: [
        "モーション軽減では移動を取り除きます。",
        "アニメーションがなくても状態が見えます。",
      ],
    },
    radius: {
      description:
        "控えめな角丸スケールがコンテナを柔らかく、しかし正確に保ちます。",
      intent:
        "すべてのコントロールをピル形にせずに、包含と入れ子を表現します。",
      rules: [
        "大きい面の内側では小さい半径を使います。",
        "ピル形はステータス表示に残します。",
        "継ぎ目は直角に保ちます。",
      ],
      uses: ["タグと断片", "コントロール", "カード", "ダイアログ"],
      accessibility: [
        "形だけを状態の手がかりにしません。",
        "フォーカスリングは見えている輪郭に沿います。",
      ],
    },
    spacing: {
      description:
        "4ピクセルのスケールが決定を区切り、関連するコンテンツをまとめます。",
      intent: "枠線やカード、装飾的な塗りを足す前に、リズムで階層を作ります。",
      rules: [
        "文書化されたステップだけを使います。",
        "兄弟要素の間はマージンよりgapを使います。",
        "セクションのリズムは視覚的に調整します。",
      ],
      uses: [
        "密なラベル",
        "コントロール内部",
        "関連コンテンツ",
        "大きな区切り",
      ],
      accessibility: [
        "タッチターゲットの間隔を十分に保ちます。",
        "テキストが伸びても間隔がつぶれません。",
      ],
    },
    state: {
      description:
        "共通のライフサイクルが、待機・使用可能・空・回復可能を区別します。",
      intent:
        "何が起きているか、何が安全か、どの操作で回復できるかを示します。",
      rules: [
        "更新中も以前のデータを保持します。",
        "スケルトンは形が分かる領域にだけ使います。",
        "エラーは回復手段のそばに置きます。",
      ],
      uses: ["使えるデータなし", "以前のデータを保持", "回復可能"],
      accessibility: [
        "状態の変化を適切に通知します。",
        "意図せずフォーカスが移動しません。",
      ],
    },
    "voice-and-tone": {
      description:
        "率直で落ち着いた言葉が、リスクと文脈に応じて強調を変えます。",
      intent: "あいまいな高揚感や非難なしに、判断と回復を助けます。",
      rules: [
        "結果から伝えます。",
        "対象と結果を具体的に名指しします。",
        "緊急度は実際のリスクに合わせます。",
      ],
      uses: ["日常の案内", "確定した結果", "回復の案内"],
      accessibility: [
        "慣用句を避けます。",
        "緊急の文言も記号の工夫なしで理解できます。",
      ],
    },
    writing: {
      description:
        "具体的なインターフェイス文言が、操作・結果・状態・回復経路をそのまま名指しします。",
      intent: "次の判断を平易な能動態で書き、解釈の余地を減らします。",
      rules: [
        "能動態を使います。",
        "操作には動詞と結果を含めて名前を付けます。",
        "エラーの回復はその場で案内します。",
      ],
      uses: ["操作ラベル", "回復の文言", "次の操作"],
      accessibility: [
        "ラベルは画面の文脈がなくても意味が通ります。",
        "翻訳で文の断片をつなぎ合わせません。",
      ],
    },
  },
} satisfies DocsContent;

export default jp;
